import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Copy, Check, Mail, Settings } from "lucide-react";
import { toast } from "sonner";

const BCC_ADDRESS = "log@inbound.braincandy.gr";

export function EmailSettings() {
  const [copied, setCopied] = useState(false);
  const [autoClose, setAutoClose] = useState(true);
  const [storeEmailBodies, setStoreEmailBodies] = useState(false);
  const [zapierWebhookUrl, setZapierWebhookUrl] = useState("");

  const copyBccAddress = async () => {
    try {
      await navigator.clipboard.writeText(BCC_ADDRESS);
      setCopied(true);
      toast.success("BCC address copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy BCC address");
    }
  };

  const testZapierWebhook = async () => {
    if (!zapierWebhookUrl) {
      toast.error("Please enter your Zapier webhook URL");
      return;
    }

    try {
      await fetch(zapierWebhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "no-cors",
        body: JSON.stringify({
          from: "test@braincandy.gr",
          to: ["recipient@example.com"],
          cc: [],
          bcc: [BCC_ADDRESS],
          subject: "Test Email for Activity Logging",
          timestamp: new Date().toISOString(),
          test: true
        }),
      });

      toast.success("Test request sent to Zapier. Check your Zap history to confirm it was triggered.");
    } catch (error) {
      toast.error("Failed to send test request to Zapier webhook");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Activity Logging
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm font-medium">BCC Address for Logging</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Add this address as BCC when sending emails to automatically log them as activities.
            </p>
            <div className="flex items-center gap-2">
              <Input 
                value={BCC_ADDRESS} 
                readOnly 
                className="bg-muted"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={copyBccAddress}
                className="flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800 font-medium mb-1">Mailgun Setup Required</p>
              <p className="text-sm text-amber-700">
                Configure Mailgun to forward emails to: 
                <code className="bg-amber-100 px-1 rounded text-xs">
                  https://smtjbmylhvcxhnigwsji.supabase.co/functions/v1/email-inbound
                </code>
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-close">Auto-close "Send email" activities</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically mark matching "Send email" activities as done when emails are sent (48h window)
                </p>
              </div>
              <Switch
                id="auto-close"
                checked={autoClose}
                onCheckedChange={setAutoClose}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="store-bodies">Store email bodies</Label>
                <p className="text-sm text-muted-foreground">
                  Store email content for reference (headers only by default)
                </p>
              </div>
              <Switch
                id="store-bodies"
                checked={storeEmailBodies}
                onCheckedChange={setStoreEmailBodies}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Mailgun Setup (Recommended)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Setup Steps:</h4>
            <ol className="space-y-2 text-sm text-muted-foreground ml-4">
              <li>1. Πήγαινε στο <a href="https://mailgun.com" target="_blank" className="text-primary underline">Mailgun</a> και δημιούργησε λογαριασμό</li>
              <li>2. Προσθήκη domain: <code className="bg-muted px-1 rounded text-xs">inbound.braincandy.gr</code></li>
              <li>3. Ρύθμιση DNS records όπως σου δείχνει το Mailgun</li>
              <li>4. Πήγαινε στο <strong>Receiving → Routes</strong></li>
              <li>5. Δημιούργησε route:
                <br />• <strong>Expression:</strong> <code className="bg-muted px-1 rounded text-xs">match_recipient("log@inbound.braincandy.gr")</code>
                <br />• <strong>Action:</strong> <code className="bg-muted px-1 rounded text-xs">forward("https://smtjbmylhvcxhnigwsji.supabase.co/functions/v1/email-inbound")</code>
              </li>
              <li>6. Στείλε test email στο log@inbound.braincandy.gr για έλεγχο</li>
            </ol>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800 font-medium mb-1">💡 Tip</p>
            <p className="text-sm text-blue-700">
              Το Mailgun έχει δωρεάν tier με 5,000 emails/μήνα που είναι αρκετά για logging.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            How It Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Automatic Activity Creation</h4>
              <ul className="space-y-1 text-muted-foreground ml-4">
                <li>• Add the BCC address when sending emails from your email client</li>
                <li>• Or use Zapier to automatically forward emails with the BCC address</li>
                <li>• Activities are created automatically with all recipients linked</li>
                <li>• Recipients are matched to existing People in your database</li>
                <li>• Multiple activities are created if recipients belong to different clients</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Initiative Tagging</h4>
              <ul className="space-y-1 text-muted-foreground ml-4">
                <li>• Use [INIT:CODE] or #CODE in email subject to link to initiatives</li>
                <li>• Example: "Meeting follow-up [INIT:PROJ2024]" links to initiative PROJ2024</li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium mb-2">Privacy & Security</h4>
              <ul className="space-y-1 text-muted-foreground ml-4">
                <li>• Only emails from @braincandy.gr are processed</li>
                <li>• BCC address must be present for logging to occur</li>
                <li>• Email headers and metadata are stored, not full content</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}